import { useState, useEffect, useRef } from "react";
import { Play, Pause, RotateCcw, Maximize, Volume2, VolumeX, Captions, X } from "lucide-react";

const A = "/prezentacja";
const SCENES = [
  { img: `${A}/00-intro.jpg`, dur: 10, overlay: false, narration: "Sieć światłowodowa to tysiące połączeń. FiberPass sprawia, że masz je wszystkie pod kontrolą." },
  { img: `${A}/01-mapa.jpg`, dur: 26, title: "Mapa GIS", subtitle: "Cała sieć · słupy, kable, studnie, mufy, szafy · import CSV/KML · auto-trasowanie",
    narration: "Wszystko zaczyna się od mapy. Cała infrastruktura — słupy, kable, studnie, mufy i szafy — w jednym, żywym obrazie." },
  { img: `${A}/08-klienci.jpg`, dur: 26, title: "Klienci i ONU", subtitle: "Pełna diagnostyka: moc sygnału · temperatura · status · numer seryjny ONU",
    narration: "Każdy klient i każde ONU z pełną diagnostyką: moc sygnału, temperatura, status i numer seryjny." },
  { img: `${A}/09-trasa.jpg`, dur: 32, title: "Trasa sygnału", subtitle: "Ścieżka sygnału klient → OLT z dokładną długością łącza i przybliżeniem na mapie",
    narration: "Jednym kliknięciem prześledzisz trasę sygnału od klienta aż do OLT — z dokładną długością łącza i przybliżeniem na mapie." },
  { img: `${A}/10-lacze.jpg`, dur: 28, title: "Łącze między mufami", subtitle: "Zestawienie łącza między dwiema mufami · automatyczna najkrótsza droga światłowodu",
    narration: "Zestaw łącze między dwiema mufami, a system sam wyznaczy najkrótszą drogę światłowodu." },
  { img: `${A}/03-spawy.jpg`, dur: 28, title: "Mufy i spawy", subtitle: "Interaktywny schemat · automat 1:1 · splittery · 12 kolorów · eksport PNG",
    narration: "Zajrzyj do wnętrza mufy — interaktywny schemat spawów, splittery i standard dwunastu kolorów." },
  { img: `${A}/04-pdf.jpg`, dur: 26, title: "Dokumentacja PDF", subtitle: "Paszport sieci · protokoły PDF · komplet powykonawczy ZIP",
    narration: "Dokumentacja gotowa w sekundę: paszport sieci, protokoły PDF i komplet powykonawczy." },
  { img: `${A}/11-instalatorzy.jpg`, dur: 28, title: "Instalatorzy w terenie", subtitle: "Lokalizacja monterów na żywo · historia trasy GPS",
    narration: "Wiesz, gdzie są Twoi ludzie — lokalizacja instalatorów w terenie na żywo, wraz z historią trasy." },
  { img: `${A}/05-czat.jpg`, dur: 26, title: "Monter i czat", subtitle: "Aplikacja mobilna PWA · tryb offline · dwukierunkowy czat z biurem",
    narration: "Monter pracuje mobilnie, offline, w stałym kontakcie z biurem przez wbudowany czat." },
  { img: `${A}/12-snmp2.jpg`, dur: 30, title: "Monitoring SNMP", subtitle: "Dane z OLT · status ONU · ruch na interfejsach w czasie rzeczywistym",
    narration: "Podłącz się do OLT po SNMP — dane urządzeń, status ONU i ruch na interfejsach w czasie rzeczywistym." },
  { img: `${A}/06-kamery.jpg`, dur: 22, title: "Kamery ODF", subtitle: "Podgląd kamer IP na żywo · 4 szafy naraz · strumień RTSP",
    narration: "A gdy trzeba spojrzeć w teren — podgląd kamer w szafach, na żywo." },
  { img: `${A}/99-outro.jpg`, dur: 8, overlay: false, narration: "FiberPass. Twoja sieć. Pod pełną kontrolą." },
];

export default function Presentation() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [voice, setVoice] = useState(false);
  const [subs, setSubs] = useState(true);
  const voiceRef = useRef(false);
  const audioRef = useRef(null);
  useEffect(() => { voiceRef.current = voice; if (!voice && audioRef.current) audioRef.current.pause(); }, [voice]);

  const s = SCENES[idx];

  useEffect(() => {
    if (!playing) return undefined;
    const cur = SCENES[idx];
    if (voiceRef.current && audioRef.current) {
      const a = audioRef.current;
      a.src = `${A}/audio/${idx + 1}.mp3`;
      a.currentTime = 0;
      a.play().catch(() => {
        if (cur.narration && "speechSynthesis" in window) {
          try { window.speechSynthesis.cancel(); const u = new SpeechSynthesisUtterance(cur.narration); u.lang = "pl-PL"; window.speechSynthesis.speak(u); } catch { /* noop */ }
        }
      });
    }
    const t = setTimeout(() => {
      if (idx < SCENES.length - 1) setIdx((i) => i + 1);
      else setPlaying(false);
    }, cur.dur * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, idx]);

  const stopVoice = () => { try { window.speechSynthesis.cancel(); } catch { /* noop */ } if (audioRef.current) audioRef.current.pause(); };
  const start = () => { setIdx(0); setPlaying(true); };
  const toggle = () => { if (playing) { setPlaying(false); stopVoice(); } else { if (idx >= SCENES.length - 1) setIdx(0); setPlaying(true); } };
  const restart = () => { stopVoice(); setIdx(0); setPlaying(true); };
  const goFull = () => { const el = document.documentElement; if (el.requestFullscreen) el.requestFullscreen().catch(() => {}); };

  const btn = "flex items-center gap-1.5 rounded-full px-3 py-2 text-sm bg-slate-800/80 hover:bg-slate-700 text-slate-100 backdrop-blur transition-colors";

  return (
    <div className="fixed inset-0 bg-black overflow-hidden select-none" data-testid="presentation">
      <style>{`
        @keyframes fppZoom { from { transform: scale(1.04); } to { transform: scale(1.13); } }
        @keyframes fppBar { from { width: 0%; } to { width: 100%; } }
        @keyframes fppIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <img key={idx} src={s.img} alt="" onError={(e) => { e.target.style.opacity = 0; }}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ animation: playing ? `fppZoom ${s.dur}s ease forwards` : "none" }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 90% at 50% 40%, rgba(11,17,32,0.15) 0%, rgba(11,17,32,0.65) 100%)" }} />

      {playing && (
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-white/10 z-20">
          <div key={idx} className="h-full bg-cyan-400" style={{ animation: `fppBar ${s.dur}s linear forwards` }} />
        </div>
      )}
      <div className="absolute top-4 right-5 z-20 flex gap-1.5">
        {SCENES.map((_, i) => <span key={i} className={`w-2 h-2 rounded-full ${i === idx ? "bg-cyan-400" : "bg-white/25"}`} />)}
      </div>

      {s.overlay !== false && (
        <div key={`t${idx}`} className="absolute left-[6%] bottom-[22%] z-20 max-w-[70%]" style={{ animation: "fppIn 0.7s ease both" }}>
          <div className="text-cyan-300 text-sm font-semibold tracking-[0.25em] uppercase mb-2">FiberPass</div>
          <h2 className="text-white font-bold leading-tight" style={{ fontSize: "clamp(32px, 5vw, 68px)", textShadow: "0 4px 24px rgba(0,0,0,0.6)" }}>{s.title}</h2>
        </div>
      )}

      {subs && s.subtitle && (
        <div key={`s${idx}`} className="absolute left-0 right-0 bottom-[12%] z-20 px-[7%] text-center" style={{ animation: "fppIn 0.6s ease both" }}>
          <span className="inline-block bg-black/55 text-white rounded-lg px-4 py-2 backdrop-blur" style={{ fontSize: "clamp(15px, 1.7vw, 24px)", textShadow: "0 2px 8px rgba(0,0,0,0.7)" }}>{s.subtitle}</span>
        </div>
      )}

      {!playing && idx === 0 && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          <div className="text-center max-w-lg px-8 py-8 bg-black/60 rounded-2xl backdrop-blur" style={{ animation: "fppIn 0.6s ease both" }}>
            <h1 className="text-white font-bold mb-3" style={{ fontSize: "clamp(30px,4vw,52px)" }}>Prezentacja FiberPass</h1>
            <p className="text-slate-300 text-sm mb-6 leading-relaxed">Film trwa około 5 minut. Kliknij <b>Odtwórz</b>, włącz <b>Pełny ekran</b> i nagraj ekran w rozdzielczości 1920×1080 (np. OBS lub wbudowany rejestrator).<br />Aby mieć dźwięk lektora, włącz <b>Lektor</b> i nagrywaj z dźwiękiem systemu.</p>
            <button onClick={start} data-testid="presentation-play" className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-full px-6 py-3 text-lg">
              <Play size={22} /> Odtwórz
            </button>
          </div>
        </div>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 flex-wrap justify-center">
        <button onClick={toggle} data-testid="presentation-toggle" className={btn}>{playing ? <><Pause size={16} /> Pauza</> : <><Play size={16} /> Odtwórz</>}</button>
        <button onClick={restart} data-testid="presentation-restart" className={btn}><RotateCcw size={16} /> Od nowa</button>
        <button onClick={() => setVoice((v) => !v)} data-testid="presentation-voice" className={`${btn} ${voice ? "ring-1 ring-cyan-400 text-cyan-300" : ""}`}>{voice ? <Volume2 size={16} /> : <VolumeX size={16} />} Lektor</button>
        <button onClick={() => setSubs((v) => !v)} data-testid="presentation-subs" className={`${btn} ${subs ? "ring-1 ring-cyan-400 text-cyan-300" : ""}`}><Captions size={16} /> Napisy</button>
        <button onClick={goFull} data-testid="presentation-fullscreen" className={btn}><Maximize size={16} /> Pełny ekran</button>
        <a href="/" data-testid="presentation-close" className={btn}><X size={16} /> Wyjdź</a>
      </div>
      <audio ref={audioRef} preload="auto" />
    </div>
  );
}
